import {
  CertificateStatus,
  CountryIso3166,
  FieldConstraint,
  InformationRequestStatus,
  Operation,
  Activity as PrismaActivity,
} from "@tfg/db"

//Based on https://www.notion.so/Operation-chains-257a97e0d2eb454e9e023212b2528bfc

export type ItemType = "product" | "component" | "material"
export type OperationNodeId = `${ItemType}-${string}-${number}`

export const TraceabilityStatus = {
  PENDING: "pending",
  ACTIVE: "active",
  ERROR: "error",
} as const

export type TraceabilityStatus =
  (typeof TraceabilityStatus)[keyof typeof TraceabilityStatus]

export type OperationChainActivities = {
  activityId: string
  activityName: string
  type: ItemType
  position: number
}[]

export const CustomFieldConstraint = {
  STRING: "STRING",
  NUMBER: "NUMBER",
  SELECT: "SELECT",
  MULTI_SELECT: "MULTI_SELECT",
  PERCENTAGE: "PERCENTAGE",
  STANDARD: "STANDARD",
} as const

export type typeTextContent = {
  originalText: string
  TextTranslation: {
    translatedLanguage: string
    translation: string
  }[]
}

export enum CustomFieldValueRequirement {
  OPTIONAL = "OPTIONAL",
  REQUIRED = "REQUIRED",
}

export enum CustomFieldDocumentRequirement {
  NONE = "NONE",
  OPTIONAL = "OPTIONAL",
  REQUIRED = "REQUIRED",
}

export namespace TraceabilityResponses {
  export interface CommonTraceabilityRequestItem {
    id: number
    operations: TraceabilityRequestOperation[]
  }

  export interface CustomFieldValue {
    id: string
    value: string
    customFieldDefinitionId: number
    operationId: number
    document: {
      id: string
      originalName: string | null
    } | null
  }

  export interface TraceabilityRequestOperation {
    id: number
    activity: PrismaActivity
    reference: string | null
    productionSiteId: string | null
    purchaseOrderRef: string | null
    contact: {
      id: string
      userId: string
    }
    customFieldsValues: CustomFieldValue[]
    owner: {
      id: string
      email: string
    }
    doNotKnow: boolean
    informationRequestId: number
  }

  export interface UpdateOperationResponse {
    informationRequestIds: number[]
  }

  export interface MaterialTraceabilityRequestItem extends CommonTraceabilityRequestItem {
    productName: string | null
    reference: string
    children: TraceabilityRequestItem[]
    type: "material"
    itemInformation: {
      type: typeTextContent
      proportion: number
    }
    operationChainName: string
  }

  export interface ComponentTraceabilityRequestItem extends CommonTraceabilityRequestItem {
    productName: string | null
    reference: string
    children: MaterialTraceabilityRequestItem[]
    number: number
    type: "component"
    itemInformation: { mainFabric: boolean; type: typeTextContent }
    operationChainName: string
  }

  export interface ProductTraceabilityRequestItem extends CommonTraceabilityRequestItem {
    reference: string
    name: string
    color: string | null
    collection: string | null
    brand: string | null
    marketLaunchDate: Date | null
    children: ComponentTraceabilityRequestItem[]
    type: "product"
    itemInformation: { type: typeTextContent; date?: string }
    operationChainName: string
    purchaseOrderRef: string | null
  }

  export type OperationWithCustomFieldValues = Operation &
    ({
      customFieldsValues: CustomFieldValue[]
    } | null)

  export type TraceabilityRequestItem =
    | MaterialTraceabilityRequestItem
    | ComponentTraceabilityRequestItem
    | ProductTraceabilityRequestItem

  export type PublicCustomFieldDef = {
    id: number
    forActivityId: string
    onActivityId: string | null
    textContent: typeTextContent
    message: typeTextContent | null
    constraint: FieldConstraint
    min: number | null
    max: number | null
    enumValuesTranslated: typeTextContent[]
    csvColumnName: string
    valueRequirement: keyof typeof CustomFieldValueRequirement
    documentRequirement: keyof typeof CustomFieldDocumentRequirement
  }

  export interface GetTraceabilityRequestsResponse {
    data: TraceabilityRequest[]
    total: number
  }

  export interface TraceabilityRequest {
    id: number
    item: TraceabilityRequestItem
    activityId: string
    activityName: string
    issuer: {
      organizationName: string
      name: string
      email: string
    }
    status: InformationRequestStatus
    productId: number | undefined
    productUuid: string | undefined
    productName: string | undefined
    customFieldDefinitions: PublicCustomFieldDef[]
    contactId: string
    reinforcedTraceability: boolean
    createdAt: Date
  }

  export interface ProductionSite {
    id: string
    address: string
    name: string
    countryIso3166: CountryIso3166
  }

  export interface CertificateRequest {
    id: string
    reference: string | null
    status: CertificateStatus
    type: {
      name: string
      category: string
    }
    purchaseOrder: {
      products: {
        item: {
          reference: string
        }
        color: string | null
      }[]
      reference: string
    } | null
    productionSite?: ProductionSite | null
    document?: {
      id: string
      bucketPrefix: string
      originalName: string | null
    } | null
    rejectionMessage: string | null
  }

  export interface ReopenRequest {
    count: number
  }

  export interface Stats {
    opened: number
  }

  export interface TraceabilityRequestType {
    type: ItemType
    typeTextContent: typeTextContent | undefined
  }
}

export namespace TraceabilityRequests {
  export interface ProductionSite {
    id: number
    address: string
    countryIso3166: string
    organizationId: string
  }

  export type TraceabilityRequestStatus = InformationRequestStatus

  export interface CreateProductionSite {
    name: string
    address: string
    countryIso3166: CountryIso3166
  }

  export interface EditProductionSite {
    name: string
    address: string
    countryIso3166: string
  }

  export type DeleteOperationBatch = {
    operationId: number
  }[]

  export interface NewInformationRequest {
    reference: string
    operationId: number
  }

  export interface TraceabilityRequest {
    operations: NewInformationRequest[]
  }

  export interface BatchTraceabiityRequest {
    id: number
    request: TraceabilityRequest
  }
}
